import { Component } from "@mzw/component";
import { sectionRenderer } from "@mzw/section-renderer";
import { debounce, onDocumentReady, startViewTransition } from "@mzw/utilities";

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = "q";

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} ComponentFiltersRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<ComponentFiltersRefs>}
 */
export class ComponentFilters extends Component {
	requiredRefs = ["facetsForm"];

	/**
	 * Creates URL parameters from form data
	 * @param {FormData} [formData] - Optional form data to use instead of the main form
	 * @returns {URLSearchParams} The processed URL parameters
	 */
	createURLParameters(formData = new FormData(this.refs.facetsForm)) {
		const newParameters = new URLSearchParams(/** @type any */ (formData));

		if (newParameters.get("filter.v.price.gte") === "") newParameters.delete("filter.v.price.gte");
		if (newParameters.get("filter.v.price.lte") === "") newParameters.delete("filter.v.price.lte");

		newParameters.delete("page");

		const searchQuery = this.#getSearchQuery();
		if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

		return newParameters;
	}

	/**
	 * Gets the search query parameter from the current URL
	 * @returns {string} The search query
	 */
	#getSearchQuery() {
		const url = new URL(window.location.href);
		return url.searchParams.get(SEARCH_QUERY) ?? "";
	}

	get sectionId() {
		const id = this.getAttribute("section-id");
		if (!id) throw new Error("Section ID is required");
		return id;
	}

	/**
	 * Updates the URL hash with current filter parameters
	 */
	#updateURLHash() {
		const url = new URL(window.location.href);
		const urlParameters = this.createURLParameters();

		url.search = "";
		for (const [param, value] of urlParameters.entries()) {
			url.searchParams.append(param, value);
		}

		history.pushState({ urlParameters: urlParameters.toString() }, "", url.toString());
	}

	/** @type {HTMLFormElement} */
	form;
	/** @type {NodeListOf<HTMLDetailsElement>} */
	detailElements;

	get orientation() {
		return this.getAttribute("orientation");
	}

	connectedCallback() {
		super.connectedCallback();
		onDocumentReady(this.#init);
	}

	#init = () => {
		this.form = this.querySelector("form");
		this.detailElements = this.querySelectorAll("details");
		if (this.orientation === "horizontal") {
			document.body.addEventListener("click", this.handleClickOutside);
		}
	};

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.orientation === "horizontal") {
			document.body.removeEventListener("click", this.handleClickOutside);
		}
	}

	handleClickOutside = (event) => {
		if (!event.target.closest("component-filters details[open]")) {
			for (const detail of this.detailElements) {
				detail.open = false;
			}
		}
	};

	/**
	 * Updates filters and renders the section
	 */
	updateFilters = () => {
		this.#updateURLHash();
		// this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
		this.#updateSection();
	};

	/**
	 * Updates the section
	 */
	#updateSection() {
		const viewTransition = !this.closest("dialog");

		if (viewTransition) {
			startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ["product-grid"]);
		} else {
			sectionRenderer.renderSection(this.sectionId);
		}
	}

	/**
	 * Updates filters based on a provided URL
	 * @param {string} url - The URL to update filters with
	 */
	updateFiltersByURL(url) {
		history.pushState("", "", url);
		// this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
		this.#updateSection();
	}

	/**
	 * Handles mouseover events on facet labels
	 * @param {MouseEvent} event - The mouseover event
	 */
	prefetchPage = debounce((event) => {
		if (!(event.target instanceof HTMLElement)) return;

		const formData = new FormData(this.form);
		const inputElement = event.target.querySelector("input");

		if (!(inputElement instanceof HTMLInputElement)) return;

		if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

		const facetsForm = this.closest("component-filters");
		if (!(facetsForm instanceof ComponentFilters)) return;

		const urlParameters = facetsForm.createURLParameters(formData);

		const url = new URL(window.location.pathname, window.location.origin);

		for (const [key, value] of urlParameters) url.searchParams.append(key, value);

		if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

		sectionRenderer.getSectionHTML(this.sectionId, true, url);
	}, 200);

	cancelPrefetchPage = () => this.prefetchPage.cancel();
}

if (!customElements.get("component-filters")) {
	customElements.define("component-filters", ComponentFilters);
}

export class FacetRemove extends HTMLElement {
	/** @type {HTMLAnchorElement} */
	link;

	connectedCallback() {
		onDocumentReady(this.#init);
	}

	#init = () => {
		this.link = this.querySelector("a");
		this.link.setAttribute("role", "button");
		this.addEventListener("click", this);
	};

	disconnectedCallback() {
		this.removeEventListener("click", this);
	}

	handleEvent(event) {
		if (event.type === "click") {
			const filters =
				this.closest("component-filters:defined") ||
				document.querySelector("component-filters:defined");

			if (!filters) return;

			if (!this.link.href) return;

			event.preventDefault();

			filters.updateFiltersByURL(this.link.href);
		}
	}
}

if (!customElements.get("facet-remove")) {
	customElements.define("facet-remove", FacetRemove);
}
